'use client'

import dynamic from 'next/dynamic'
import { memo, useCallback, useEffect, useMemo } from 'react'

// server action
import { trackLastJobSeen } from '@/app/[slug]/actions'

// services
import { IExtraCompanyInfo, IJobDetail } from '@/services/jobs/type'
import { viewJob } from '@/services/jobs'
import ModelError from '@/services/model/error'

// context
import { useAppStore } from '@/app-context'

// common helper
import { ELanguage, VNW_JOBSEEKER_URL } from '@/common'
import { slugify } from '@/helpers'
import BannerTypeB from '../ai-cv-review/BannerTypeB'

// split code
/**
 * for ssr = true
 */
const Col = dynamic(async () => (await import('@microfrontends/react-components/components/LayoutSystem/Col')))
const Row = dynamic(async () => (await import('@microfrontends/react-components/components/LayoutSystem/Row')))
const Container = dynamic(async () => (await import('./styled')).Container)
const RighContainer = dynamic(
    async () => (await import('./styled')).RighContainer
)
const LeftContainer = dynamic(
    async () => (await import('./styled')).LeftContainer
)
const BasicInformation = dynamic(() => import('./basic-info'))
const DescriptionInfo = dynamic(() => import('./description-info'))
const Benefit = dynamic(() => import('./benefit'))
const ExtraInfo = dynamic(() => import('./extra-info'))
const WorkingLocations = dynamic(() => import('./working-locations'))
const UserTags = dynamic(() => import('../user-tags'))
const CompanyInfo = dynamic(() => import('../company'))
const Breadcrumd = dynamic(() => import('../breadcrumb'))
const GoogleJobPosting = dynamic(() => import('../google-job-posting'))
const AiReviewBlock = dynamic(() => import('../ai-cv-review'))
/**
 * for ssr = false
 */
const OtherJobs = dynamic(() => import('../other-job-in-company'), {
    ssr: false
})
const ReportJob = dynamic(() => import('../report-job'))
const JobSimilar = dynamic(() => import('../job-similar'), {
    ssr: false
})
const JobWillLove = dynamic(() => import('../job-will-love'), {
    ssr: false
})
const BottomSheetApplyJob = dynamic(() => import('../bottom-sheet-apply-job'), {
    ssr: false
})
const ApplyModal = dynamic(() => import('../apply-modal'), {
    ssr: false
})
const MailboxJobAlert = dynamic(() => import('../mailbox-job-alert'), {
    ssr: false
})
const IncreaseView = dynamic(() => import('./increase-view'), { ssr: false })

interface IJobDetailContainerProps {
    jobDetail: Partial<IJobDetail>
    userIp: string[]
}

function JobDetailContainer(props: IJobDetailContainerProps) {
    const { userIp, jobDetail } = props
    const { langCode } = useAppStore().props
    const { user } = useAppStore().state

    const company: Partial<IExtraCompanyInfo> = useMemo(
        () => ({
            ...jobDetail?.companyInfo,
            companyName: jobDetail?.companyName,
            companyLogo: jobDetail?.companyLogo,
            companyProfileBannerUrl:
                jobDetail?.companyInfo?.companyProfileBannerUrl,
            companyUrl: `${VNW_JOBSEEKER_URL}/${
                langCode === ELanguage.VI ? 'nha-tuyen-dung' : 'companies'
            }/${slugify(jobDetail?.companyName)}-c${
                jobDetail?.companyId
            }?fromPage=jobDetail`, // params fromPage using for case reject redirect to CCP
            companyCCPUrl: jobDetail?.companyInfo?.companyProfileUrl,
            address: jobDetail?.address,
            companySize:
                langCode === ELanguage.VI
                    ? jobDetail?.companySizeVI
                    : jobDetail?.companySize,
            contactName: jobDetail?.contactName,
            allowRedirect: jobDetail?.companyInfo?.allowRedirect
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [jobDetail]
    )

    const doViewJob = useCallback(async () => {
        try {
            if (!jobDetail?.jobId) return
            trackLastJobSeen(jobDetail.jobId, user?.userId)

            if (!user?.accessToken) return
            const view = await viewJob(user?.accessToken, jobDetail.jobId)

            if (view instanceof ModelError) {
                return
            }

            return view
        } catch (error) {
            console.error(error, 'error doViewJob')
        }
    }, [user?.accessToken, user?.userId, jobDetail.jobId])

    useEffect(() => {
        doViewJob()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.accessToken])

    return (
        <Container gutter={10} suppressHydrationWarning={true}>
            <Col xs={24} sm={16}>
                <Row gutter={{ xs: 8, sm: 10 }}>
                    <Col>
                        <RighContainer>
                            <BasicInformation companyInfo={company} userIp={userIp} />
                            <BannerTypeB />
                            <DescriptionInfo />
                            <AiReviewBlock userIp={userIp} />
                            <Benefit />
                            <ExtraInfo />
                            <WorkingLocations />
                            <UserTags />
                        </RighContainer>
                    </Col>
                    <Col>
                        <JobWillLove />
                    </Col>
                    <Col>
                        <Breadcrumd />
                    </Col>
                </Row>
            </Col>

            <Col xs={24} sm={8}>
                <LeftContainer>
                    <CompanyInfo companyInfo={company} />
                    <ReportJob company={company} />
                    <JobSimilar />
                    {!jobDetail?.isAnonymous ? (
                        <OtherJobs companyUrl={company.companyUrl} />
                    ) : null}

                    <Breadcrumd isMobile />
                </LeftContainer>
            </Col>

            <BottomSheetApplyJob
                allowRedirectJobIds={company.allowRedirectJobIds}
            />
            <ApplyModal />
            <MailboxJobAlert />
            <GoogleJobPosting company={company} />
            <IncreaseView userIp={userIp} />
        </Container>
    )
}

export default memo(JobDetailContainer)
